import 'dayjs/locale/en-gb';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import DepartmentDTO from '@src/adapters/dto/department/DepartmentDTO';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import useSaveDepartmentLazy from '@src/adapters/hooks/department/useSaveDepartment';
import DepartmentRoleDTO from '@src/adapters/dto/department/DepartmentRoleDTO';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { da } from 'date-fns/locale';

interface Props {
  department?: DepartmentDTO;
  open: boolean;
  close(department: DepartmentDTO | undefined): void;
}

function AddEdit({ department, open, close }: Props) {
  const { execute: save } = useSaveDepartmentLazy();
  const [newRole, setNewRole] = useState('');
  const [activeRoles, setActiveRoles] = useState<DepartmentRoleDTO[]>([]);
  const [inactiveRoles, setInactiveRoles] = useState<DepartmentRoleDTO[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DepartmentDTO>({
    defaultValues: department,
  });

  useEffect(() => {
    if (department) {
      reset(department);
    }
  }, [department, reset]);

  useEffect(() => {
    if (department && department.roles && department?.roles.length > 0) {
      setActiveRoles(department.roles.filter((r) => r.isActive));
      setInactiveRoles(department.roles.filter((r) => !r.isActive));
    }
  }, [department]);

  const onSubmit = async (data: DepartmentDTO) => {
    if (activeRoles.length === 0 && data.isActive) {
      enqueueSnackbar(
        'At least one role must be active to save an active department',
        { variant: 'error' }
      );
      return;
    }

    data.roles = [
      ...activeRoles.map((ar) => {
        return { id: ar.id, name: ar.name, isActive: true };
      }),
      ...inactiveRoles.map((ia) => {
        return { id: ia.id, name: ia.name, isActive: false };
      }),
    ];
    const result = await save({
      department: data,
    });
    if (result.department) {
      reset();
      setActiveRoles([]);
      setInactiveRoles([]);
      close(result.department);
    }
  };

  const handleAddRole = () => {
    if (
      !activeRoles.some((r) => r.name === newRole) &&
      !inactiveRoles.some((r) => r.name === newRole) &&
      newRole
    ) {
      setActiveRoles((prev) => [
        ...prev,
        { id: undefined, name: newRole, isActive: true },
      ]);
      setNewRole('');
    }
  };

  const handleClose = () => {
    reset();
    setActiveRoles([]);
    setInactiveRoles([]);
    close(undefined);
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="Note Details"
      aria-describedby="Note Details"
      fullWidth={true}
      maxWidth={'md'}>
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}>
        <Typography
          variant="h6"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
          }}>
          {department ? 'Edit Department' : 'Add Department'}
        </Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent
          sx={{
            width: '100%',
          }}>
          <Grid container spacing={2}>
            <Grid size={8}>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ ...params }) => (
                  <TextField
                    {...params.field}
                    label="Name"
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                    error={!!errors.name}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <Box>
                            {errors.name && (
                              <PriorityHighIcon color="inherit" />
                            )}
                          </Box>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                name="isActive"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Is Active"
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="New Role"
                sx={{
                  width: '100%',
                  marginTop: '20px',
                  marginBottom: '20px',
                }}
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Button
                        onClick={handleAddRole}
                        color="primary"
                        variant="outlined"
                        sx={{ flex: '1', marginLeft: '8px' }}>
                        Add
                      </Button>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Autocomplete
                    multiple
                    id="active-roles"
                    options={activeRoles}
                    getOptionLabel={(option) => option.name}
                    onChange={(event, newValue, reason, details) => {
                      if (
                        reason === 'removeOption' &&
                        details &&
                        details.option
                      ) {
                        setInactiveRoles((prev) => [...prev, details.option]);
                      }
                      setActiveRoles(newValue);
                    }}
                    value={activeRoles}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Active Roles"
                      />
                    )}
                  />
                </Grid>
                <Grid size={6}>
                  <Autocomplete
                    multiple
                    id="inactive-roles"
                    options={activeRoles}
                    getOptionLabel={(option) => option.name}
                    onChange={(event, newValue, reason, details) => {
                      if (
                        reason === 'removeOption' &&
                        details &&
                        details.option
                      ) {
                        setActiveRoles((prev) => [...prev, details.option]);
                      }
                      setInactiveRoles(newValue);
                    }}
                    value={inactiveRoles}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Inactive Roles"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flex: '1', marginRight: '8px' }}>
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddEdit;
